import { useEffect, useState, useRef, useCallback } from 'react';
import {SidePanel} from "spark-core-dx/components";
import { useCoreContext } from "spark-core-dx/contexts";
import { useDashboardDomainContext } from '../../DashboardDomainProvider';
import { useGlobalFilters } from '../../hooks/useGlobalFilters';
import { FilterFields } from './FilterFields';
import { CalendarInputs } from './CalendarInputs';
import { DateInformation } from './DateInformation';
import { DateRangeSelector } from './DateRangeSelector';
import moment from 'moment';
import './GlobalFilterPanel.scss';

const quickRangeOptions = [
	"Single Date",
	"Custom Range",
	"Previous Full Week",
	"Week to Date",
	"Previous Full 2 Weeks",
	"2 Weeks to Date",
	"Previous Full Month",
	"Month to Date",
	"Previous Full Quarter",
	"Quarter to Date",
	"Previous Full Year",
	"Year to Date",
]

// TODO: remove date picker and add immediate calendar popup 
const calculateDateRange = (rangeType) => {
	let startDate, endDate;

	switch (rangeType) {
		case "Single Date":
			return { startDate: '', endDate: '' };
		case "Custom Range":
			return { startDate: '', endDate: '' };
		case "Previous Full Week":
			startDate = moment().subtract(1, 'week').startOf('week');
			endDate = moment().subtract(1, 'week').endOf('week');
			break;
		case "Week to Date":
			startDate = moment().startOf('week');
			endDate = moment();
			break;
		case "Previous Full 2 Weeks":
			startDate = moment().subtract(2, 'weeks').startOf('week');
			endDate = moment().subtract(1, 'week').endOf('week');
			break;
		case "2 Weeks to Date":
			startDate = moment().subtract(2, 'weeks').startOf('week');
			endDate = moment();
			break;
		case "Previous Full Month":
			startDate = moment().subtract(1, 'month').startOf('month');
			endDate = moment().subtract(1, 'month').endOf('month');
			break;
		case "Month to Date":
			startDate = moment().startOf('month');
			endDate = moment();
			break;
		case "Previous Full Quarter":
			startDate = moment().subtract(1, 'quarter').startOf('quarter');
			endDate = moment().subtract(1, 'quarter').endOf('quarter');
			break;
		case "Quarter to Date":
			startDate = moment().startOf('quarter');
			endDate = moment();
			break;
		case "Previous Full Year":
			startDate = moment().subtract(1, 'year').startOf('year');
			endDate = moment().subtract(1, 'year').endOf('year');
			break;
		case "Year to Date":
			startDate = moment().startOf('year');
			endDate = moment();
			break;
		default:
			return { startDate: '', endDate: '' };
	}

	return {
		startDate: startDate.format('YYYY-MM-DD'),
		endDate: endDate.format('YYYY-MM-DD')
	};
};

export const GlobalFilterPanel = ({
	open,
	onUpdateSidePanel,
	setOpenEditSidePanel,
	title,
}) => {
	const { Profile } = useCoreContext();
	const { globalFilterOptions } = useGlobalFilters();
	const { dashboardUserStateLib, setIsFilterDebouncing } = useDashboardDomainContext();
	const { activeUserDashState, userDashStates, saveUserDashState } = dashboardUserStateLib;
	const [currentDashState, setCurrentDashState] = useState(activeUserDashState);
	const [activeCalendar, setActiveCalendar] = useState(false);
	const [isFranchisee, setIsFranchisee] = useState(false);
	const [selectedDateRange, setSelectedDateRange] = useState(null);
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');

	const debounceTimer = useRef(null);

	const [selectedFilters, setSelectedFilters] = useState({
		franchiseeFilters: [],
		customerFilters: []
	});

	useEffect(() => {
		setIsFranchisee(!globalFilterOptions?.find(x => x.dataKey === "global_tenant_options")?.records?.length <= 1);
	}, [globalFilterOptions])

	// Save date to global filters when the state changes
	useEffect(() => {
		setCurrentDashState(prev => ({
			...prev,
			GlobalFilters: { startDate, endDate, selectedRange: selectedDateRange }
		}));
	}, [startDate, endDate, selectedDateRange]);

	const cancelDebounce = useCallback(() => {
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
			setIsFilterDebouncing(false);
		}
	}, [setIsFilterDebouncing]);

	// Update state filter variables for franchisee + customer
	const handleDataUpdate = useCallback((filterValues, filterType) => {
		console.log('Updating data:', filterType, filterValues);

		// Update selectedFilters state
		if (filterType === 'franchisee') {
			setSelectedFilters(prev => ({ ...prev, franchiseeFilters: filterValues }));
		} else if (filterType === 'customer') {
			setSelectedFilters(prev => ({ ...prev, customerFilters: filterValues }));
		}
	}, []);

	// Update userDashState with new filters/data
	const handleFilterRequest = useCallback(() => {
		if (debounceTimer.current) {
			clearTimeout(debounceTimer.current);
		}

		setIsFilterDebouncing(true);

		debounceTimer.current = setTimeout(() => {
			const globalFilters = [];

			// Add franchisee filters
			if (selectedFilters.franchiseeFilters && selectedFilters.franchiseeFilters.length > 0) {
				const franchiseeFilters = selectedFilters.franchiseeFilters
					.map(filter => ({ label: filter, type: 'franchisee' }));
				globalFilters.push(...franchiseeFilters);
			}

			// Add customer filters
			if (selectedFilters.customerFilters && selectedFilters.customerFilters.length > 0) {
				const customerFilters = selectedFilters.customerFilters
					.map(filter => ({ label: filter, type: 'customer' }));
				globalFilters.push(...customerFilters);
			}

			// Add date range as a filter object in global filters if valid start date
			if (startDate || endDate || selectedDateRange) {
				globalFilters.push({
					type: 'dateRange',
					startDate: startDate,
					endDate: endDate,
					selectedRange: selectedDateRange
				});
			}

			const updatedUserDashState = {
				...activeUserDashState,
				GlobalFilters: globalFilters
			};

			setCurrentDashState(updatedUserDashState);
			saveUserDashState(updatedUserDashState);

			// update user dash state
			setIsFilterDebouncing(false);
		}, 300);
	}, [setIsFilterDebouncing, selectedFilters, startDate, endDate, selectedDateRange]);

	// On date range click
	const handleOnRangeClick = (option) => {

		if (option === selectedDateRange) {
			setSelectedDateRange(null);
			setStartDate('')
			setEndDate('')
			setActiveCalendar(false);
			handleFilterRequest();
		} else {
			setSelectedDateRange(option)
			if (option === "Single Date" || option === "Custom Range") {
				setStartDate('');
				setEndDate('');
				setActiveCalendar(true);
			} else {
				const dateRange = calculateDateRange(option);
				setStartDate(dateRange.startDate);
				setEndDate(dateRange.endDate);
				setActiveCalendar(false);
				handleFilterRequest();
			}
		}
	};

	// dates update via useffect
	const handleEndDateChange = (e) => {
		const newEndDate = e.target.value;
		setEndDate(newEndDate);
	};

	const handleStartDateChange = (e) => {
		const newStartDate = e.target.value;

		if (selectedDateRange === "Single Date") {
			setStartDate(newStartDate);
			setEndDate(newStartDate);
		} else {
			setStartDate(newStartDate);
		}
	};

	const handleCalendarButtonClick = () => {
		setSelectedDateRange("Custom Range");
		setActiveCalendar(!activeCalendar);
	};

	// Set active user global filters
	useEffect(() => {
		if (activeUserDashState) {
			const globalFilters = activeUserDashState.GlobalFilters || [];
			setSelectedFilters({
				franchiseeFilters: globalFilters.filter(f => f.type === 'franchisee').map(f => f.value),
				customerFilters: globalFilters.filter(f => f.type === 'customer').map(f => f.value)
			});
		}
	}, [activeUserDashState]);

	const getFilterDataByKey = (datakey) => {
		const filterOptions = globalFilterOptions?.find(option =>
			option.dataKey === datakey
		);

		let mappedFilterOptions = filterOptions?.records.map(record => {
			if (datakey === 'global_customer_options') {
				return {
					Text: record.NAME,
					Value: record.ID
				};
			} else if (datakey === 'global_tenant_options') {
				return {
					Text: record.NAME,
					Value: record.ID
				};
			}
			return record;
		});

		// Remove duplicates in data based on text (already done for franchisee)
		const uniqueFilterOptions = mappedFilterOptions?.filter((item, index, self) =>
			index === self.findIndex(t => t.Text === item.Text))

		return uniqueFilterOptions;
	};

	// Set active user global filters
	useEffect(() => {
		//console.log("dash state after save", currentDashState)
	}, [currentDashState]);

	return (
		<SidePanel
			open={open}
			title={title}
			onCancel={() => setOpenEditSidePanel(false)}
			onUpdateSidePanel={onUpdateSidePanel}
			fieldName="SidePanelFormWidth">

			{/** Global Filters */}
			<FilterFields
				isFranchisee={isFranchisee}
				selectedFilters={selectedFilters}
				getFilterDataByKey={getFilterDataByKey}
				handleDataUpdate={handleDataUpdate}
				handleFilterRequest={handleFilterRequest}
			/>

			{/** Global Filter Side Panel Footer */}
			<div className='dashboard-filter-footer'>
				<CalendarInputs
					activeCalendar={activeCalendar}
					selectedDateRange={selectedDateRange}
					startDate={startDate}
					endDate={endDate}
					handleStartDateChange={handleStartDateChange}
					handleEndDateChange={handleEndDateChange}
					handleFilterRequest={handleFilterRequest}
					cancelDebounce={cancelDebounce}
				/>

				<DateInformation
					startDate={startDate}
					endDate={endDate}
					handleCalendarButtonClick={handleCalendarButtonClick}
				/>

				<DateRangeSelector
					quickRangeOptions={quickRangeOptions}
					selectedDateRange={selectedDateRange}
					handleOnRangeClick={handleOnRangeClick}
				/>
			</div>

		</SidePanel>
	);
};